import React, { useEffect, useMemo, useState } from 'react';
import { Calendar, Check, Play, Star, Tv, X } from 'lucide-react';
import WatchlistButton from './WatchlistButton';
import CastRail from './components/actors/CastRail';
import { useCatalog } from './providers/CatalogProvider';
import { backdropSrc, stillSrc } from './lib/images';

/**
 * Fiche d'une série — saisons et épisodes.
 *
 * Une seule vue au lieu de deux écrans empilés : les saisons deviennent des
 * onglets, la liste d'épisodes reste visible en dessous. On évite ainsi
 * l'aller-retour « liste des saisons → liste des épisodes → retour » qui
 * obligeait à repasser par un écran intermédiaire à chaque changement.
 */
export default function SeriesView({ series, onClose, onSelectEpisode, watchlist = [], onToggleWatchlist }) {
  const catalog = useCatalog();

  const bySeason = useMemo(() => {
    const map = new Map();
    for (const episode of series.episodes || []) {
      const key = episode.season || 1;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(episode);
    }
    for (const list of map.values()) {
      list.sort((a, b) => (a.episode || 0) - (b.episode || 0));
    }
    return map;
  }, [series.episodes]);

  const seasons = useMemo(() => [...bySeason.keys()].sort((a, b) => a - b), [bySeason]);
  const [season, setSeason] = useState(() => seasons[0] ?? 1);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const episodes = bySeason.get(season) || [];
  const total = series.episodes?.length || 0;

  const isWatched = (episode) => catalog?.isWatched(episode) ?? false;
  const progressOf = (episode) => catalog?.progressOf(episode) ?? 0;

  return (
    <div className="titlesheet" role="dialog" aria-modal="true" aria-label={series.title}>
      {series.backdrop && (
        <div className="titlesheet-bg" aria-hidden="true">
          <img src={backdropSrc(series.backdrop)} alt="" loading="lazy" decoding="async" />
        </div>
      )}

      <button type="button" className="titlesheet-close" onClick={onClose} aria-label="Fermer">
        <X aria-hidden="true" />
      </button>

      <div className="titlesheet-scroll">
        <div className="titlesheet-body">
          <div className="shell stack" style={{ gap: 'clamp(1.75rem, 4vw, 2.75rem)', paddingTop: 'clamp(1.5rem, 5vw, 3rem)' }}>
            {/* ── En-tête ── */}
            <header className="serie-head">
              {series.poster && (
                <img className="serie-poster" src={series.poster} alt="" loading="eager" />
              )}

              <div className="min-w-0">
                <p className="eyebrow eyebrow-gold mb-2">
                  <Tv className="inline w-3 h-3 mr-1.5 -mt-0.5" aria-hidden="true" />
                  Série
                </p>
                <h1 className="display-md">{series.title}</h1>

                <div className="hero-facts" style={{ marginTop: 12 }}>
                  {Number(series.rating) > 0 && (
                    <span className="score">
                      <Star aria-hidden="true" />
                      {Number(series.rating).toFixed(1)}
                    </span>
                  )}
                  {series.year && <span>{series.year}</span>}
                  <span className="dot" aria-hidden="true" />
                  <span>
                    {seasons.length} saison{seasons.length > 1 ? 's' : ''}
                  </span>
                  <span className="dot" aria-hidden="true" />
                  <span>
                    {total} épisode{total > 1 ? 's' : ''}
                  </span>
                </div>

                {series.overview && <p className="hero-pitch clamp-3">{series.overview}</p>}

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {episodes[0] && (
                    <button
                      type="button"
                      className="btn btn-gold"
                      onClick={() => onSelectEpisode(episodes[0])}
                    >
                      <Play aria-hidden="true" style={{ fill: 'currentColor' }} />
                      Lire S{season}E{episodes[0].episode}
                    </button>
                  )}
                  <WatchlistButton
                    movieId={`series_${series.tmdbId}`}
                    isInWatchlist={(series.episodes || []).some((ep) => watchlist.includes(ep.id))}
                    onToggle={onToggleWatchlist}
                    size="large"
                  />
                </div>

                {series.genres?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {series.genres.map((genre) => (
                      <span key={genre} className="tag">
                        {genre}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </header>

            {/* ── Saisons ── */}
            {seasons.length > 1 && (
              <div className="stack gap-3">
                <h2 className="eyebrow">Saisons</h2>
                <div className="flex flex-wrap gap-2">
                  {seasons.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className="chip"
                      aria-pressed={value === season}
                      onClick={() => setSeason(value)}
                    >
                      Saison {value}
                      <span className="text-[var(--fs-2xs)] opacity-70">
                        ({bySeason.get(value).length})
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* ── Épisodes ── */}
            <section className="stack gap-2">
              <h2 className="section-title" style={{ fontSize: 'var(--fs-xl)' }}>
                {episodes.length} épisode{episodes.length > 1 ? 's' : ''}
              </h2>

              <ul className="ep-list">
                {episodes.map((episode) => {
                  const seen = isWatched(episode);
                  const progress = progressOf(episode);
                  return (
                    <li key={episode.id}>
                      <button
                        type="button"
                        className={`ep${seen ? ' is-seen' : ''}`}
                        onClick={() => onSelectEpisode(episode)}
                      >
                        <span className="ep-thumb">
                          {episode.episodeStill || episode.backdrop ? (
                            <img
                              src={stillSrc(episode.episodeStill || episode.backdrop)}
                              alt=""
                              loading="lazy"
                              decoding="async"
                            />
                          ) : (
                            <Tv className="w-5 h-5 text-[var(--t-faint)]" aria-hidden="true" />
                          )}
                          <span className="ep-play" aria-hidden="true">
                            <Play />
                          </span>
                          {progress > 0 && progress < 98 && (
                            <span className="poster-progress">
                              <i style={{ width: `${progress}%` }} />
                            </span>
                          )}
                        </span>

                        <span className="ep-body">
                          <span className="ep-title">
                            <b>{episode.episode}.</b> {episode.episodeTitle || `Épisode ${episode.episode}`}
                            {seen && <Check className="ep-check" aria-hidden="true" />}
                          </span>
                          {episode.episodeOverview && (
                            <span className="ep-text clamp-2">{episode.episodeOverview}</span>
                          )}
                          <span className="ep-meta">
                            {episode.duration && <span>{episode.duration}</span>}
                            {episode.episodeAirDate && (
                              <>
                                <span className="dot" aria-hidden="true" />
                                <span className="inline-flex items-center gap-1">
                                  <Calendar className="w-3 h-3" aria-hidden="true" />
                                  {new Date(episode.episodeAirDate).toLocaleDateString('fr-FR')}
                                </span>
                              </>
                            )}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>

            {/* ── Distribution ── */}
            <CastRail
              tmdbId={series.tmdbId}
              type="serie"
              fallbackCast={series.cast || []}
              director={series.director}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
